import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, Bot, Coins, Trophy, Users, AlertTriangle } from "lucide-react";

export default function TermsPage() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isRu = i18n.language === "ru";

  const content = isRu ? {
    title: "Пользовательское соглашение",
    subtitle: "Условия использования TeaChess",
    lastUpdated: "Последнее обновление: 23 февраля 2026",
    sections: [
      {
        icon: Shield,
        title: "1. Общие положения",
        content: `TeaChess — это онлайн-платформа для игры в шахматы на виртуальные баллы (Points). 
        
Используя наш сервис, вы соглашаетесь с данными условиями. Если вы не согласны, пожалуйста, не используйте платформу.

Мы оставляем за собой право изменять условия в любое время. Актуальная версия всегда доступна на этой странице.`
      },
      {
        icon: Coins,
        title: "2. Система баллов (Points)",
        content: `• 1 балл (Point) = 1 российский рубль (RUB)
• Пополнение возможно в различных валютах по курсу ЦБ РФ
• Поддерживаемые валюты: RUB, USD, EUR, KZT, BYN, UAH, CNY и другие
• Приветственный бонус: 500 баллов новым пользователям
• Бонусные баллы можно вывести после 10 сыгранных игр; минимальная сумма вывода — 500 баллов
• Комиссия платформы: 5% от выигрыша в играх на ставки`
      },
      {
        icon: Bot,
        title: "3. Игры с ботами",
        content: `ВАЖНО: Наша платформа использует компьютерных ботов для обеспечения игрового процесса 24/7.

• При поиске соперника, если реальный игрок не найден в течение 10 секунд, вам будет предложена игра с ботом
• Боты имеют реалистичные имена и рейтинги, соответствующие вашему уровню игры
• Боты играют на основе шахматного движка Stockfish с настроенной сложностью
• В турнирах присутствует около 20 ботов для обеспечения постоянной игры
• Игры с ботами на ставки работают так же, как игры с реальными игроками — вы можете выиграть или проиграть

Мы стремимся к прозрачности и честно сообщаем о наличии ботов на платформе.`
      },
      {
        icon: Trophy,
        title: "4. Турниры",
        content: `• Турниры работают 24/7 и всегда доступны для участия
• В каждом турнире участвуют реальные игроки и боты
• Призовой фонд формируется из вступительных взносов
• Распределение призов: 1 место — 50%, 2 место — 30%, 3 место — 20%
• Платформа берёт 10% комиссии с призового фонда платных турниров
• Бесплатные турниры доступны для отработки навыков`
      },
      {
        icon: Users,
        title: "5. Честная игра",
        content: `• Запрещено использование шахматных программ и подсказок
• Наша система анти-чита анализирует ходы с помощью ИИ
• При обнаружении нечестной игры аккаунт блокируется
• Заблокированные средства не возвращаются
• Решение администрации о блокировке является окончательным`
      },
      {
        icon: AlertTriangle,
        title: "6. Ответственная игра",
        content: `• TeaChess — это развлекательная платформа
• Играйте только на те средства, которые готовы потерять
• Устанавливайте личные лимиты на депозиты и время игры
• Если вы чувствуете зависимость от игры, обратитесь за помощью
• Минимальный возраст для игры на ставки: 18 лет

Мы не несём ответственности за финансовые потери пользователей.`
      }
    ],
    agreeText: "Продолжая использовать TeaChess, вы подтверждаете, что прочитали и согласны с данными условиями.",
    backButton: "Назад"
  } : {
    title: "Terms of Service",
    subtitle: "TeaChess Usage Agreement",
    lastUpdated: "Last updated: February 23, 2026",
    sections: [
      {
        icon: Shield,
        title: "1. General Terms",
        content: `TeaChess is an online platform for playing chess using virtual points.
        
By using our service, you agree to these terms. If you disagree, please do not use the platform.

We reserve the right to modify terms at any time. The current version is always available on this page.`
      },
      {
        icon: Coins,
        title: "2. Points System",
        content: `• 1 Point = 1 Russian Ruble (RUB)
• Deposits accepted in various currencies at CBR exchange rates
• Supported currencies: RUB, USD, EUR, KZT, BYN, UAH, CNY, and more
• Welcome bonus: 500 points for new users
• Bonus points can be withdrawn after 10 games played; minimum withdrawal is 500 points
• Platform fee: 5% of winnings in stake games`
      },
      {
        icon: Bot,
        title: "3. Bot Games",
        content: `IMPORTANT: Our platform uses computer bots to ensure 24/7 gameplay.

• When searching for opponents, if no real player is found within 10 seconds, you'll be offered a bot game
• Bots have realistic names and ratings matching your skill level
• Bots play using the Stockfish chess engine with adjusted difficulty
• Tournaments include approximately 20 bots to ensure constant gameplay
• Stake games against bots work the same as against real players — you can win or lose

We strive for transparency and honestly disclose the presence of bots on the platform.`
      },
      {
        icon: Trophy,
        title: "4. Tournaments",
        content: `• Tournaments run 24/7 and are always available
• Each tournament includes real players and bots
• Prize pool is formed from entry fees
• Prize distribution: 1st place — 50%, 2nd — 30%, 3rd — 20%
• Platform takes 10% commission from paid tournament prize pools
• Free tournaments available for practice`
      },
      {
        icon: Users,
        title: "5. Fair Play",
        content: `• Use of chess programs and hints is prohibited
• Our anti-cheat system analyzes moves using AI
• Accounts found cheating will be banned
• Blocked funds are not refundable
• Administration's decision on bans is final`
      },
      {
        icon: AlertTriangle,
        title: "6. Responsible Gaming",
        content: `• TeaChess is an entertainment platform
• Only play with funds you can afford to lose
• Set personal limits on deposits and play time
• Seek help if you feel addicted to gaming
• Minimum age for stake games: 18 years

We are not responsible for users' financial losses.`
      }
    ],
    agreeText: "By continuing to use TeaChess, you confirm that you have read and agree to these terms.",
    backButton: "Back"
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl" data-testid="terms-page">
      <Button 
        variant="ghost" 
        onClick={() => navigate(-1)}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        {content.backButton}
      </Button>

      <Card>
        <CardHeader className="text-center border-b">
          <CardTitle className="text-2xl">{content.title}</CardTitle>
          <p className="text-muted-foreground">{content.subtitle}</p>
          <p className="text-xs text-muted-foreground mt-2">{content.lastUpdated}</p>
        </CardHeader>
        <CardContent className="pt-6">
          <ScrollArea className="h-[600px] pr-4">
            <div className="space-y-8">
              {content.sections.map((section, index) => (
                <div key={index} className="space-y-3">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <section.icon className="h-5 w-5 text-primary" />
                    {section.title}
                  </h2>
                  <div className="pl-7 text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
                    {section.content}
                  </div>
                </div>
              ))}
              
              <div className="mt-8 p-4 bg-primary/10 rounded-lg border border-primary/20">
                <p className="text-sm text-center">
                  {content.agreeText}
                </p>
              </div>
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
